import {
  Linkedin,
  Twitter,
  Instagram,
  Youtube,
  Facebook,
  Rss,
} from "lucide-react"
import { Link } from "react-router-dom"
import { companies } from "@/data/companyData"

export const Footer = () => {
  return (
    <footer className="bg-gradient-to-b from-[#1a1a1a] to-[#111] text-white py-16 border-t border-white/5 relative z-20">
      <div className="section-container">
        <div className="flex flex-col lg:flex-row justify-between items-start gap-12">
          {/* Logo & Positioning Section */}
          <div className="mb-4 lg:mb-0 max-w-sm">
            <Link to="/" className="inline-block mb-4">
              <img
                src="/rsa-logo.png"
                alt="RSA Tech Group - Home"
                className="h-10 w-auto brightness-0 invert opacity-90 transition-opacity"
                width={125}
                height={40}
                loading="lazy"
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed font-light mb-4">
              The AI Holding Company.
            </p>
            {/* Compliance Badge */}
            <img
              src="/Drata.jpeg"
              alt="SOC 2, HIPAA, ISO 27001 & GDPR Compliance"
              className="max-w-[120px] rounded-lg opacity-90 hover:opacity-100 transition-opacity"
              style={{ mixBlendMode: 'lighten' }}
              width={120}
              height={120}
              loading="lazy"
            />
          </div>

          {/* Links Section — 3 columns */}
          <div className="w-full lg:w-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 lg:gap-x-12 gap-y-6">
              {/* Column 1: Our Companies */}
              <div className="flex flex-col gap-3">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#FDA400] mb-1">Our Companies</span>
                {companies.map((company) =>
                  company.externalUrl ? (
                    <a
                      key={company.name}
                      href={company.externalUrl}
                      target="_blank"
                      rel="noopener"
                      className="text-white font-medium hover:text-accent transition-colors text-sm"
                    >
                      {company.name}
                    </a>
                  ) : (
                    <Link key={company.name} to={company.href} className="text-white font-medium hover:text-accent transition-colors text-sm">
                      {company.name}
                    </Link>
                  )
                )}
              </div>

              {/* Column 2: Company */}
              <div className="flex flex-col gap-3">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#FDA400] mb-1">Company</span>
                <Link to="/about" className="text-white font-medium hover:text-accent transition-colors text-sm">About</Link>
                <Link to="/leadership-team" className="text-white font-medium hover:text-accent transition-colors text-sm">Leadership</Link>
                <Link to="/partners" className="text-white font-medium hover:text-accent transition-colors text-sm">Partners</Link>
                <Link to="/careers" className="text-white font-medium hover:text-accent transition-colors text-sm">Careers</Link>
                <Link to="/methodology" className="text-white font-medium hover:text-accent transition-colors text-sm">Methodology</Link>
              </div>

              {/* Column 3: Resources */}
              <div className="flex flex-col gap-3">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#FDA400] mb-1">Resources</span>
                <Link to="/blogs-insights" className="text-white font-medium hover:text-accent transition-colors text-sm">Blogs</Link>
                <Link to="/news-insights" className="text-white font-medium hover:text-accent transition-colors text-sm">News</Link>
                <Link to="/events" className="text-white font-medium hover:text-accent transition-colors text-sm">Events</Link>
                <Link to="/contact" className="text-white font-medium hover:text-accent transition-colors text-sm">Contact</Link>
                <Link to="/faq" className="text-white font-medium hover:text-accent transition-colors text-sm">FAQ</Link>
              </div>
            </div>
          </div>

          {/* Social Icons */}
          <nav aria-label="Social media links">
            <div className="flex gap-6 text-accent">
              <a
                href="https://www.instagram.com/rsatech"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
                aria-label="Follow RSA Tech Group on Instagram"
              >
                <Instagram className="w-6 h-6" aria-hidden="true" />
              </a>
              <a
                href="https://www.linkedin.com/company/rsa-tech"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
                aria-label="Follow RSA Tech Group on LinkedIn"
              >
                <Linkedin className="w-6 h-6" aria-hidden="true" />
              </a>
              <a
                href="https://www.youtube.com/@rsatech"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
                aria-label="Subscribe to RSA Tech Group on YouTube"
              >
                <Youtube className="w-6 h-6" aria-hidden="true" />
              </a>
              <a
                href="https://twitter.com/rsatech"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
                aria-label="Follow RSA Tech Group on Twitter"
              >
                <Twitter className="w-6 h-6" aria-hidden="true" />
              </a>
              <a
                href="https://www.facebook.com/rsatech"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
                aria-label="Follow RSA Tech Group on Facebook"
              >
                <Facebook className="w-6 h-6" aria-hidden="true" />
              </a>
              <a
                href="/blogs-insights"
                className="hover:text-white transition-colors"
                aria-label="RSA Tech Group Blogs & Insights"
              >
                <Rss className="w-6 h-6" aria-hidden="true" />
              </a>
            </div>
          </nav>
        </div>
      </div>
      <div className="section-container mt-16 pt-8 border-t border-gray-800 text-center text-gray-400 text-sm">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p>&copy; {new Date().getFullYear()} RSA Tech Group. All rights reserved.</p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link to="/privacy-policy" className="hover:text-blue-500 transition-colors">Privacy Policy</Link>
            <Link to="/cookie-policy" className="hover:text-blue-500 transition-colors">Cookie Policy</Link>
            <Link to="/terms-of-use" className="hover:text-blue-500 transition-colors">Terms of Use</Link>
            <Link to="/customer-security-statement" className="hover:text-blue-500 transition-colors">Security</Link>
            <Link to="/human-rights-statement" className="hover:text-blue-500 transition-colors">Human Rights</Link>
            <Link to="/do-not-share" className="hover:text-blue-500 transition-colors">Do Not Share</Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
